import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
  Step,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'
import planAllowances from '../../../helpers/planAllowances.json'

// Figures come from the plan catalogue mirror, never typed in here.
const { core, premium, enterprise } = planAllowances.plans

const Onboarding = () => (
  <Page
    title="Onboarding Your Organisation"
    intro="AmbuNet sets up a new organisation in two phases: a short setup
    wizard the first time you sign in, followed by a Getting Started checklist
    that ticks itself off as you add real data. This page walks through both."
  >
    <Section title="Who can do this?">
      <p>
        Both the setup wizard and the Getting Started checklist are only shown
        to <strong>Site Administrators</strong>. Other users who sign in while
        setup is underway simply see the normal app.
      </p>
    </Section>

    <Section title="Phase 1 — the setup wizard">
      <p>
        Until initial setup is finished, Site Administrators see a four-step
        wizard instead of the normal AmbuNet screens. It captures the
        essentials your organisation can't run without:
      </p>

      <Step number={1} title="Company Info">
        <p>
          Enter your company name, short name, website, and the reply-to email
          address used on system emails. The company name, short name, and a
          valid reply-to email are required before you can continue.
        </p>
      </Step>

      <Step number={2} title="Base Location">
        <p>
          Add one or more bases — for example Headquarters, a named ambulance
          station, or a depot — with their addresses. Each base must have a
          unique name. Vehicles, equipment, staff, and event resources are all
          organised by base, so it's worth naming them the way your teams talk
          about them.
        </p>
        <p>
          The number of bases is capped by your subscription plan — Core
          includes {core.allowances.baseLocations}, Premium{' '}
          {premium.allowances.baseLocations}, and Enterprise{' '}
          {enterprise.allowances.baseLocations} — and adding a base beyond
          your plan's cap is refused. Contact AmbuNet if you need more bases
          than your plan includes.
        </p>
      </Step>

      <Step number={3} title="Teams &amp; Roles">
        <p>
          Teams control which areas of AmbuNet your staff can access, and
          clinical roles describe the grades you use for shifts, skill mixes,
          and training requirements. The core teams are built in and can't be
          removed; add any extra teams your organisation uses, and adjust the
          clinical roles list to match the grades you actually employ.
        </p>
        <p>
          You then choose <strong>your own clinical role</strong> from that
          list. Your account is created when you subscribe, before your
          organisation's roles exist, so this is where your own grade is set —
          it decides which mandatory training applies to you. If none of the
          roles fit, add one to the list first.
        </p>
      </Step>

      <Step number={4} title="Finish">
        <p>
          Press <strong>Finish Setup</strong>. Your settings are saved and you
          land on the Getting Started checklist to carry on at your own pace.
        </p>
      </Step>

      <Info title="Nothing here is final">
        Everything captured by the wizard can be changed later under{' '}
        <strong>Site Configuration</strong>.
      </Info>
    </Section>

    <Section title="Phase 2 — the Getting Started checklist">
      <p>
        After the wizard, open <DocLink url="/getting-started" text="Getting Started" />{' '}
        at any time. It lists the remaining setup jobs with a progress bar, and
        each step <strong>ticks itself off automatically</strong> as the
        matching data appears in your organisation — there's nothing to mark
        done by hand. Every step has a button that takes you straight to the
        right screen:
      </p>
      <Bullets>
        <Bullet>
          <strong>Complete your company profile</strong> — done once your
          company name, reply-to email, and company address (with postcode) are
          saved.
        </Bullet>
        <Bullet>
          <strong>Upload your logo</strong> — it appears on quotes, engagement
          letters, patient records, and the staff portal.
        </Bullet>
        <Bullet>
          <strong>Set up your base locations</strong> — done once the
          placeholder "Default" base has been renamed or given an address, or a
          real base has been added.
        </Bullet>
        <Bullet>
          <strong>Invite your team</strong> — done when at least one user
          account exists beyond your own.
        </Bullet>
        <Bullet>
          <strong>Add your vehicles</strong> — registering your fleet unlocks
          VDIs, cleaning schedules, Make Ready, and defect reporting.
        </Bullet>
        <Bullet>
          <strong>Add your first client</strong> — clients are the
          organisations you work for, used for quotes, events, invoicing, and
          the client portal.
        </Bullet>
        <Bullet>
          <strong>Plan your first event or shift</strong> — done as soon as
          your first event or shift exists.
        </Bullet>
        <Bullet>
          <strong>Configure mandatory training</strong> — done once at least
          one course has a renewal frequency or role requirement set.
        </Bullet>
      </Bullets>
      <p>
        Nothing on the checklist is compulsory — use <strong>Skip</strong> on
        any step that doesn't apply to your organisation (and{' '}
        <strong>Unskip</strong> if you change your mind).
      </p>
      <Info title="The card on the Home Screen">
        While setup is incomplete, Site Administrators also see a{' '}
        <strong>Finish setting up your organisation</strong> card at the top of
        the Home Screen showing progress and the next step, with a{' '}
        <strong>Continue Setup</strong> button. Use the X to hide the card if
        you'd rather work from the checklist page — it disappears for good once
        every step is complete or skipped.
      </Info>
      <Info title="Completion sticks">
        Once every step is complete or skipped, the checklist is marked
        finished permanently — it won't reappear if data is later archived.
      </Info>
    </Section>

    <Section title="Setting up base locations well">
      <p>
        Two details are worth getting right when you revisit your bases under{' '}
        <DocLink url="/base-locations" text="Base Locations" />
        :
      </p>
      <Bullets>
        <Bullet>
          <strong>Pick the address from the address search</strong> rather than
          typing it in — this also stores a map point for the site. The map
          point is what allows Patient Transport tariffs to charge mileage
          base-to-base (the full round trip from leaving the base to returning
          to it), which many operators use for ad-hoc transport journeys.
        </Bullet>
        <Bullet>
          <strong>On-Call Number</strong> — a shared standby line for the base.
          If set, it's called instead of an individual's personal mobile from
          the Home Screen's On Call Now card, if that site runs on-call rotas.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Next steps">
      <p>
        Once setup is complete, continue with{' '}
        <DocLink
          docsPage
          url="/employees-hr/adding-employees"
          text="Adding Employees"
        />{' '}
        to build out your workforce, and see{' '}
        <DocLink
          docsPage
          url="/getting-started/home-screen"
          text="Home Screen"
        />{' '}
        for what your staff will see when they sign in.
      </p>
    </Section>
  </Page>
)

export default Onboarding
